import { preloadFont } from 'troika-three-text';

import config from '@app/config';

import Base from './Base';
// TODO: We should replace this with `<SimpleBlobBg />`.
import { BackgroundScene } from './components';
import BlurBackground from './components/BlurBackground';
import CardFloorGameDown from './components/CardGL/CardFGDown';
import CardFloorGameUp from './components/CardGL/CardFGUp';
import CardFire from './components/CardGL/Fire';
import DebugScene from './components/DebugScene/';
import YatCube from './components/YatCube';
import LayerManager from './globals/LayerManager';
import Renderer from './globals/Renderer';
import { LID_LAYER_ID } from './shared';
import { FloorGameWebGLVariant } from './types';
import PerformanceProfile from './utils/PerformanceProfile';

declare interface CommonScenes {
    'gradient-background': unknown;
    'blur-background': unknown;
}

// Handle Scene Creations and Common Items (Renderer, etc) and own the Layer Manager
export default class World extends Base {
    renderer: Renderer;
    destroyed = false;
    canUpdate: boolean;
    layerManager: LayerManager;
    devMode: boolean;
    isFontLoaded: boolean;
    commonScenes!: CommonScenes;

    constructor(parentElement, isFullScreen, devMode) {
        super(parentElement, isFullScreen);
        this.renderer = Renderer.getInstance(parentElement, isFullScreen);
        this.layerManager = new LayerManager(this);

        this.canUpdate = false;
        this.devMode = devMode;

        this.commonScenes = {
            'gradient-background': {
                scene: BackgroundScene,
                zIndex: 1,
                needsUpdate: true,
                isScrollingElement: false,
            },
            'blur-background': {
                scene: BlurBackground,
                sceneOptions: this.renderer,
                needUpdate: false,
                pixelRatio: 1,
                skipPerformanceCheck: true,
                isScrollingElement: false,
            },
        };

        this.isFontLoaded = false;
        preloadFont({ font: '/assets/fonts/tusker-grotesk-4500-medium-webgl.woff' }, () => {
            this.isFontLoaded = true;
        });

        if (config.nodeEnv === 'development') {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore-disable-next-line
            window.__world = this; // Used only on DevMode
        }
    }

    async loadLiquidID() {
        const liquidIDScene = await new YatCube(this, LID_LAYER_ID);
        await liquidIDScene.init();

        this.layerManager.addLayer({
            id: LID_LAYER_ID,
            zIndex: 2,
            width: 150,
            height: 150,
            x: 0,
            y: 0,
            pixelRatio: PerformanceProfile.globalProfile.maxDpr,
            scene: liquidIDScene,
            domElement: false,
            needUpdate: true,
            needLateUpdate: true,
            isScrollingElement: false,
        });
    }

    async addLiquidIDLayer(layerID, domElement) {
        const liquidIDScene = await new YatCube(this, layerID);
        await liquidIDScene.init();

        // TODO THUNDER: Share assets

        this.layerManager.addLayer({
            id: layerID,
            zIndex: 2,
            width: 150,
            height: 150,
            x: 0,
            y: 0,
            pixelRatio: PerformanceProfile.globalProfile.maxDpr,
            scene: liquidIDScene,
            domElement: false,
            needUpdate: true,
            needLateUpdate: true,
            isScrollingElement: true,
        });

        this.layerManager.layers[layerID].updateDomElement(domElement);
    }

    destroy() {
        this.destroyed = true;
        super.destroy(this.renderer);
    }

    update(elapsed: number, delta: number) {
        if (!this.canUpdate) return;
        super.update(elapsed, delta);

        PerformanceProfile.startFPSCheck();

        this.layerManager.render(elapsed, delta);

        PerformanceProfile.endFPSCheck();
    }

    enableRendering() {
        this.canUpdate = true;
    }

    disableRendering() {
        this.canUpdate = false;
    }

    // Common Layers used throughout the website
    addCommonLayer(id) {
        const layerProperties = this.commonScenes[id];

        this.layerManager.addLayer({
            id: id,
            zIndex: layerProperties.zIndex || 1,
            width: 1,
            height: 1,
            x: 0,
            y: 0,
            pixelRatio: layerProperties.pixelRatio || PerformanceProfile.globalProfile.maxDpr,
            skipPerformanceCheck: layerProperties.skipPerformanceCheck,
            scene: new layerProperties.scene(layerProperties.sceneOptions),
            needUpdate: layerProperties.needUpdate,
            isScrollingElement: layerProperties.isScrollingElement,
        });

        return this.layerManager.layers[id];
    }

    checkCommonLayer(id) {
        if (this.commonScenes[id]) return this.addCommonLayer(id);
        else return false;
    }

    // Card Layers (Floor Game, ...)
    newCardLayer(id, domElement, options) {
        let card;

        if (options.type === FloorGameWebGLVariant.TierUp) {
            card = new CardFloorGameUp(this.renderer, options);
        } else if (options.type === FloorGameWebGLVariant.TierDown) {
            card = new CardFloorGameDown(this.renderer, options);
        } else if (options.type === FloorGameWebGLVariant.OnFire) {
            card = new CardFire(this.renderer, options);
        } else if (!options.type) {
            // eslint-disable-next-line no-console
            console.log('No Card Type Specified');
        }

        this.layerManager.addLayer({
            id: id,
            zIndex: 2,
            width: 1,
            height: 1,
            x: 0,
            y: 0,
            pixelRatio: PerformanceProfile.globalProfile.maxDpr,
            scene: card,
            needUpdate: true,
            fadeBottom: options.isDetailView || options.fadeBottom,
            borderRadius: options.borderRadius,
            isScrollingElement: !options.isCollectionView,
            type: options.type || FloorGameWebGLVariant.TierDown,
        });
        this.layerManager.layers[id].updateDomElement(domElement);
    }

    // Empty Layer for Debug
    newDebugLayer(id, domElement) {
        const debugScene = new DebugScene();
        this.layerManager.addLayer({
            id: id,
            zIndex: 1,
            width: 1,
            height: 1,
            x: 0,
            y: 0,
            pixelRatio: 1.0,
            scene: debugScene,
        });
        this.layerManager.layers[id].updateDomElement(domElement);
    }

    enableLayer(id) {
        if (this.layerManager.layers[id]) this.layerManager.enableLayer(id);
        else {
            // eslint-disable-next-line no-console
            if (this.devMode) console.warn(`⛔️ WebGL Layer "${id}" Not Found ⛔️`);
        }
    }

    disableLayer(id) {
        if (this.layerManager.layers[id]) this.layerManager.disableLayer(id);
        else {
            // eslint-disable-next-line no-console
            if (this.devMode) console.warn(`⛔️ WebGL Layer "${id}" Not Found ⛔️`);
            return false;
        }
    }
}
